<template>
    <TextLineLayout
        ref="refLines"
        v-for="(pos, i) in positions"
        :text="text"
        :bounds="textBounds"
        :position="pos"
        :key="`textLine${i}`"
        :dir="dir"
        :debug="false"
        :layer="i === highlightIndex && highlightFg ? LAYER_FG : LAYER_BG"
        :options="{
            strokeColor: color,
            ...(i === highlightIndex ? { strokeWidth: 0, fillOpacity: 1 } : {}),
        }"
    />
</template>

<script setup>
    import { computed, reactive, ref } from 'vue';

    import { useThreeObject } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import TextLineLayout from './TextLineLayout.vue';
    import { useBounds } from '~/components/gl/Bounds';
    import { LAYER_BG, LAYER_FG } from '~/core/constants';

    defineProps({
        highlightFg: { default: true },
        highlightIndex: { default: 2 },
        color: { default: 'white' },
        text: { default: 'Chase' },
    });

    const visible = ref(false);

    const dir = ref(1);
    const totalLines = 5;
    const centerIndex = Math.floor(totalLines / 2);

    const positions = ref([]);
    const refLines = ref([]);

    useThreeObject(null, { name: 'TextRows', props: { v: visible } });

    const bounds = useBounds((val) => resize(val));

    const textBounds = computed(() => {
        const { width, height } = bounds;
        const expandedHeight = height * 1.35;
        const textHeight = expandedHeight / totalLines;
        const textWidth = width;
        return { width: textWidth, height: textHeight };
    });

    const totalHeight = computed(() => textBounds.value.height * totalLines);

    positions.value = new Array(totalLines).fill().map((_, i) => ({ x: 0, y: 0 }));

    let tl;
    const show = ({ delay = 0, type = '1', timeScale = 1 } = {}) => {
        tl?.kill();

        tl = new gsap.timeline({
            delay,
            onStart: () => (visible.value = true),
            onComplete: () => (visible.value = false),
            onUpdate: () => {
                refLines.value.forEach((line, i) => line.updateLetters());
            },
        });
        tl.timeScale(timeScale);

        // Choose between animations
        const animation = Math.random() > 0.5 ? animation1 : animation2;

        animation();

        return tl;
    };

    const animation1 = () => {
        const inDur = 0.7;
        const outDur = 0.4;
        const outDelay = 0.8;
        const { width, height } = bounds;

        tl.addLabel('start', 0);
        tl.addLabel('end', outDelay);

        refLines.value.forEach((line, i) => {
            const stagger = i * 0.03;
            const newIndex = centerIndex - i;
            const y = newIndex * textBounds.value.height;

            const { object } = line;
            const { realSize, letters } = line.text;

            const xFrom = (width * -0.5 + realSize.width * -0.5) * dir.value;
            const xTo = (width * 0.5 - realSize.width * 0.5) * dir.value;
            const xEnd = width * 0.5 * dir.value;

            tl.fromTo(
                object.position,
                { x: xFrom, y: y },
                { x: xTo, duration: inDur, ease: 'power3.out' },
                `start+=${stagger}`
            );
            tl.to(
                object.position,
                { x: xEnd, duration: outDur, ease: 'power2.in' },
                `end+=${stagger}`
            );

            const letterDist = width * 0.2 * dir.value;

            tl.set(letters, { alpha: 1 }, 'start');
            tl.to(letters, { alpha: 0, duration: 0.2 }, 'end+=0.2');

            letters.forEach((letter, i) => {
                const reverseIndex = letters.length - i;

                const letterStagger = reverseIndex * 0.015;
                tl.fromTo(
                    letter.position,
                    { x: -letterDist },
                    { x: 0, duration: inDur, ease: 'power3.out' },
                    `start+=${letterStagger}`
                );
                tl.to(
                    letter.position,
                    { x: letterDist, duration: outDur, ease: 'power2.in' },
                    `end+=${letterStagger}`
                );
            });
        });
    };

    const animation2 = () => {
        const inDur = 0.7;
        const outDur = 0.4;
        const outDelay = 0.8;
        const { height } = bounds;

        tl.addLabel('start', 0);
        tl.addLabel('end', outDelay);

        const centerIndex = Math.floor(refLines.value.length / 2);

        refLines.value.forEach((line, i) => {
            const newIndex = centerIndex - i;
            const yTo = height * 0.5 * newIndex;

            const { object } = line;
            const { letters } = line.text;

            const yFrom = newIndex * textBounds.value.height;
            tl.fromTo(
                object.position,
                { x: 0, y: yFrom },
                { y: yTo, duration: outDur, ease: 'back.in(1.2)' },
                `end`
            );

            tl.set(letters, { alpha: 1 }, 'start');
            tl.to(letters, { alpha: 0, duration: 0.2 }, 'end+=0.2');

            const yFromLetter = height * 1.2;

            letters.forEach((letter, i) => {
                const letterStagger = i * 0.01;
                tl.fromTo(
                    letter.position,
                    { x: 0, y: yFromLetter },
                    { y: 0, duration: inDur, ease: 'back.out(1.3)' },
                    `start+=${letterStagger}`
                );
            });
        });
    };

    const resize = async ({ width, height }) => {
        positions.value.forEach((pos, i) => {
            const newIndex = centerIndex - i;
            const y = newIndex * textBounds.value.height;
            pos.y = y;
        });
    };

    defineExpose({ show });
</script>
